import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { ModalService } from '../../../../core/services/modal.service';
import { LoadService } from '../../../../core/services/load.service';
import { AuthService } from '../../../../core/services/auth.service';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { LoadWithView, Load } from '../../../../core/models/load.model';

import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { FormFieldComponent } from '../../../../shared/ui/forms/form-field/form-field.component';
import { SelectComponent, SelectOption } from '../../../../shared/ui/forms/select/select.component';
import { TextareaComponent } from '../../../../shared/ui/forms/textarea/textarea.component';
import { RadioCardGroupComponent, RadioOption } from '../../../../shared/ui/forms/radio-card-group/radio-card-group.component';
import { TextComponent } from '../../../../shared/ui/typography/text/text.component';
import { LucideIconComponent } from '../../../../shared/ui/lucide-icon/lucide-icon.component';

export type CancellationCategory = 'entry_error' | 'cancelled_by_us' | 'cancelled_by_counterparty';

@Component({
  selector: 'app-cancel-load-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    FormFieldComponent,
    SelectComponent,
    TextareaComponent,
    RadioCardGroupComponent,
    TextComponent,
    LucideIconComponent,
  ],
  templateUrl: './cancel-load-modal.component.html',
})
export class CancelLoadModalComponent {
  private readonly modalService = inject(ModalService);
  private readonly loadService = inject(LoadService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  protected readonly modalData = this.modalService.state()?.data as {
    load: LoadWithView | Load;
    onDeleted?: () => void;
    onCancelled?: () => void;
  };

  protected readonly load = this.modalData?.load;
  protected readonly isSubmitting = signal(false);

  readonly categoryOptions: RadioOption[] = [
    {
      value: 'cancelled_by_us',
      label: 'Cancelled by Us (Carrier)',
      description: 'Internal cancellation (breakdown, driver unavailable, resourcing). Marked Not Billable.',
      icon: 'Ban',
    },
    {
      value: 'cancelled_by_counterparty',
      label: 'Cancelled by Upstream Party',
      description: 'Broker, customer, or shipper cancelled. Sent to Invoicing for TONU fee review.',
      icon: 'Receipt',
    },
    {
      value: 'entry_error',
      label: 'Entry Error / Duplicate',
      description: 'Entered incorrectly, duplicate load, or not needed. Permanently deletes this load.',
      icon: 'Trash2',
    },
  ];

  readonly carrierReasonOptions: SelectOption[] = [
    { value: 'Equipment / Truck Breakdown', label: 'Equipment / Truck Breakdown' },
    { value: 'Driver Unavailable / Call-out', label: 'Driver Unavailable / Call-out' },
    { value: 'Schedule / Resourcing Conflict', label: 'Schedule / Resourcing Conflict' },
    { value: 'Double Booked / Overbooking', label: 'Double Booked / Overbooking' },
    { value: 'Rate Dispute / Rejection', label: 'Rate Dispute / Rejection' },
    { value: 'Safety / Weather Constraint', label: 'Safety / Weather Constraint' },
    { value: 'Other Carrier Reason', label: 'Other Carrier Reason' },
  ];

  readonly counterpartyReasonOptions: SelectOption[] = [
    { value: 'Shipper Cancelled Shipment', label: 'Shipper Cancelled Shipment' },
    { value: 'Commodity Not Ready / Delayed Facility', label: 'Commodity Not Ready / Delayed Facility' },
    { value: 'Broker Dispatched Alternate Carrier', label: 'Broker Dispatched Alternate Carrier' },
    { value: 'Missed Loading Window / Late Arrival', label: 'Missed Loading Window / Late Arrival' },
    { value: 'Counterparty Rate Cancellation', label: 'Counterparty Rate Cancellation' },
    { value: 'Facility Closed / Holiday', label: 'Facility Closed / Holiday' },
    { value: 'Other Upstream Reason', label: 'Other Upstream Reason' },
  ];

  protected form: FormGroup = this.fb.group({
    category: ['cancelled_by_us' as CancellationCategory, [Validators.required]],
    carrierReason: ['Equipment / Truck Breakdown'],
    counterpartyReason: ['Shipper Cancelled Shipment'],
    notes: [''],
  });

  protected selectedCategory = signal<CancellationCategory>('cancelled_by_us');

  constructor() {
    this.form.get('category')?.valueChanges.subscribe((cat: CancellationCategory) => {
      this.selectedCategory.set(cat);
      if (cat === 'cancelled_by_us') {
        this.form.get('carrierReason')?.setValidators([Validators.required]);
        this.form.get('counterpartyReason')?.clearValidators();
      } else if (cat === 'cancelled_by_counterparty') {
        this.form.get('counterpartyReason')?.setValidators([Validators.required]);
        this.form.get('carrierReason')?.clearValidators();
      } else {
        this.form.get('carrierReason')?.clearValidators();
        this.form.get('counterpartyReason')?.clearValidators();
      }
      this.form.get('carrierReason')?.updateValueAndValidity();
      this.form.get('counterpartyReason')?.updateValueAndValidity();
    });
  }

  close(): void {
    this.modalService.close();
  }

  async confirm(): Promise<void> {
    if (!this.load) return;

    const cat = this.selectedCategory();
    const companyId = this.authService.companyId() || undefined;

    this.isSubmitting.set(true);

    try {
      if (cat === 'entry_error') {
        // Permanently delete the load
        await this.loadService.deleteLoad(this.load.id, companyId);
        this.toastService.show('success', 'Load deleted permanently.', {
          title: 'Load Deleted',
          duration: 4000,
        });
        this.modalService.close();
        if (this.modalData?.onDeleted) {
          this.modalData.onDeleted();
        } else {
          this.router.navigate(['/loads']);
        }
      } else if (cat === 'cancelled_by_us') {
        // Cancel on carrier's side (not billable, TONU not applicable)
        const reason = this.form.get('carrierReason')?.value || 'Carrier Cancelled';
        const notes = this.form.get('notes')?.value || '';

        await this.loadService.cancelLoad(
          this.load.id,
          this.load.status,
          {
            party: 'carrier',
            reason,
            notes,
          },
          companyId
        );

        this.toastService.show('success', 'Load cancelled (internal carrier cancellation). Marked as non-billable.', {
          title: 'Load Cancelled',
          duration: 5000,
        });
        this.modalService.close();
        this.modalData?.onCancelled?.();
      } else if (cat === 'cancelled_by_counterparty') {
        // Cancel by upstream party (broker/customer/shipper) -> TONU pending
        const reason = this.form.get('counterpartyReason')?.value || 'Upstream Cancelled';
        const notes = this.form.get('notes')?.value || '';

        await this.loadService.cancelLoad(
          this.load.id,
          this.load.status,
          {
            party: 'upstream',
            reason,
            notes,
          },
          companyId
        );

        this.toastService.show(
          'success',
          'Load cancelled by upstream party. TONU review pending in Invoicing & Billing.',
          {
            title: 'Load Cancelled',
            duration: 5000,
          }
        );
        this.modalService.close();
        this.modalData?.onCancelled?.();
      }
    } catch (err: any) {
      console.error('Failed to process load cancellation/deletion:', err);
      this.toastService.show('error', err?.message || 'Failed to process load cancellation.', {
        title: 'Action Failed',
        showClose: true,
        duration: 0,
      });
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
